import React, { useState } from 'react';
import { Typography, CircularProgress } from '@mui/material';
import { AddButton } from '../button/button';
import { useDispatch, useSelector } from 'react-redux';
import Editor from "@monaco-editor/react";
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { updateCompletedTask } from '../../redux/slices/upload';
import { useParams } from 'react-router-dom';
import { STATUS } from '../../utils/status';
import { setCodeEdited } from '../../redux/reducers/isEditTaskCompleted';

interface EditCodeActionProps {
    actionValue: any[];
    result: any;
    actionIndex: number;
    action: string;
    projectStatus: string;
    heading?: string;
}

type CompileResult = {
  status?: string;
  executionTime?: number;
  stdout?: string;
  stderr?: string;
  exception?: string;
};

    const languages = [
        { label: "Java", value: "java", subValue: "java" },
        { label: "JavaScript", value: "javascript", subValue: "javascript" },
        { label: "Python & Django", value: "python", subValue: "pythondjango" },
        { label: "C++", value: "cpp", subValue: "cpp" },
        { label: "C", value: "c", subValue: "c" },
        { label: "C#", value: "csharp", subValue: "csharp" },
        { label: "TypeScript", value: "typescript", subValue: "typescript" },
        { label: "Go", value: "go", subValue: "go" },
        { label: "Rust", value: "rust", subValue: "rust" },
        { label: "PHP", value: "php", subValue: "php" },
        { label: "Shell", value: "shell", subValue: "shell" },
        { label: "Kotlin", value: "kotlin", subValue: "kotlin" },
        { label: "ObjC", value: "objective-c", subValue: "objc" },
        { label: "HTML/CSS", value: "html", subValue: "htmlcss" },
        { label: "Dart", value: "dart", subValue: "dart" },
        { label: "Ruby", value: "ruby", subValue: "ruby" },
        { label: "Tex", value: "latex", subValue: "tex" },
        { label: "Lua", value: "lua", subValue: "lua" },
        { label: "Swift (for iOS)", value: "swift", subValue: "swift" },
        { label: "Kotlin (for Android)", value: "kotlin", subValue: "kotlin" },
        { label: "Flutter", value: "dart", subValue: "flutter" },
        { label: "React Native", value: "javascript", subValue: "reactnative" },
        { label: "Vue.js", value: "javascript", subValue: "vuejs" },
        { label: "Angular", value: "typescript", subValue: "angular" },
        { label: "Next.js", value: "javascript", subValue: "nextjs" },
        { label: "Node.js", value: "javascript", subValue: "nodejs" },
        { label: "Flask", value: "python", subValue: "flask" },
        { label: "FastAPI", value: "python", subValue: "fastapi" },
        { label: "Blazor", value: "csharp", subValue: "blazor" },
        { label: "Ruby on Rails", value: "ruby", subValue: "rubyonrails" },
        { label: "Spring Boot", value: "java", subValue: "spring_boot" },
    ];

export default function EditCodeAction({ action, actionValue, result, actionIndex, projectStatus, heading }: EditCodeActionProps) {
    const isProjectStatusCompleted = projectStatus && projectStatus === 'COMPLETED'
    const [contents, setContents] = useState<any[]>(actionValue);
    const dispatch = useDispatch<AppDispatch>()
    const { id } = useParams();
    const [isEdited, setIsEdited] = useState(false);
    const { updateCompletedTaskStatus } = useSelector((state: RootState) => state.upload)
    const fetchPermissions = useSelector((state: RootState) => state.projects.permissions);
    const [currentConfig, setcurrentConfig] = useState<any>(undefined)

    if (actionValue?.length === 0) {
        return <Typography>No code available.</Typography>;
    }

    const handleChange = (valueIndex: number, newValue: string) => {
        if (isProjectStatusCompleted) return;
        const updatedContents = [...contents];
        updatedContents[valueIndex] = {
            ...updatedContents[valueIndex],
            content: newValue
        };
        setContents(updatedContents);
        setIsEdited(true);
        dispatch(setCodeEdited(true));
    };

    const handleSave = () => {
        if (isProjectStatusCompleted) return;
        const payload = {
            project_id: id,
            task_id: result.tasks[0].task_id,
            allocation_id: result.tasks[0].task_allocation_id,
            response_id: result.tasks[0].responses[0].id,
            action_id: actionIndex,
            action: action,
            action_value: {
                id: contents[0].id,
                text: contents[0].content
            }
        };
        setcurrentConfig(actionIndex)
        dispatch(updateCompletedTask(payload));
        setIsEdited(false);
        dispatch(setCodeEdited(false));
    }
    return (
        <>
            {contents.map((value: any, valueIndex: number) => {
                return (
                    <div key={valueIndex} style={{ display: 'flex', marginBottom: '10px', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontWeight: 'bold', marginRight: '10px', marginTop: '10px' }}>
                            {value.config_details?.value?.title}:
                        </Typography>
                            <Editor
                                height="400px"
                                language={languages.find((lang) => lang.subValue === value.config_details?.value?.language)?.value || "javascript"}
                                defaultValue={value.edit_code}
                                theme="vs-light"
                                options={{
                                    readOnly: fetchPermissions?.completing_task_action_details?.[1] === "read",
                                    minimap: { enabled: true },
                                    fontSize: 14,
                                    suggestOnTriggerCharacters: true,
                                    quickSuggestions: true,
                                    wordBasedSuggestions: true,
                                    tabCompletion: "on",
                                    snippetSuggestions: "inline"
                                }}
                                onChange={(e: any) => handleChange(valueIndex, e.target.value)}
                            />
                            {value.compile && (
                                <div className="w-full mt-4 bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm">
                                    <Typography variant="subtitle2" className="font-semibold mb-2 text-gray-700">
                                    Compilation Result
                                    </Typography>

                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <Typography variant="body2" className="text-gray-600">
                                        <strong>Status:</strong> {value.compile.status || "N/A"}
                                        </Typography>
                                        <Typography variant="body2" className="text-gray-600">
                                        <strong>Execution Time:</strong>{" "}
                                        {value.compile.executionTime ? `${value.compile.executionTime} ms` : "N/A"}
                                        </Typography>
                                    </div>

                                    <div>
                                        {value.compile.stderr && (
                                        <Typography variant="body2" className="text-red-600 whitespace-pre-wrap">
                                            <strong>Error:</strong> {value.compile.stderr}
                                        </Typography>
                                        )}
                                        {value.compile.stdout && (
                                        <Typography variant="body2" className="text-green-700 whitespace-pre-wrap">
                                            <strong>Output:</strong> {value.compile.stdout}
                                        </Typography>
                                        )}
                                        {value.compile.exception && (
                                        <Typography variant="body2" className="text-orange-700 whitespace-pre-wrap">
                                            <strong>Exception:</strong> {value.compile.exception}
                                        </Typography>
                                        )}
                                    </div>
                                    </div>
                                </div>
                            )}
                    </div>
                )
            })}
            {!isProjectStatusCompleted && isEdited && (
                fetchPermissions?.completing_task_action_details?.[1] === "read" ? (
                    <></>
                ) : (
                    <AddButton
                        startIcon={actionIndex === currentConfig && updateCompletedTaskStatus === STATUS.PENDING ? <CircularProgress /> : null}
                        disabled={actionIndex === currentConfig && updateCompletedTaskStatus === STATUS.PENDING}
                        onClick={() => handleSave()}
                    >
                        Save
                    </AddButton>
                )
            )}

        </>
    );
}
